import * as Papa from 'papaparse';
import { type RefObject,useCallback, useEffect, useMemo, useState } from 'react';

import { copyToClipboard } from '@/lib/utils';

import type { CellPosition } from './useTableKeyboard';

interface UseTableSelectionOptions {
  dataRows: string[][];
  columnCount: number;
  editMode: boolean;
  onCellEdit?: (rowIndex: number, colIndex: number, newValue: string) => void;
  onPasteCells?: (rowIndex: number, colIndex: number, values: string[][]) => void;
  focusRef: RefObject<HTMLDivElement>;
}

interface SelectionRange {
  anchor: CellPosition;
  end: CellPosition;
  startRow: number;
  endRow: number;
  startCol: number;
  endCol: number;
}

function parseClipboardTsv(text: string): string[][] {
  const result = Papa.parse<string[]>(text, {
    delimiter: '\t',
    skipEmptyLines: false,
    dynamicTyping: false,
  });
  const rows = (result.data ?? []) as string[][];
  while (rows.length > 0 && rows[rows.length - 1].every((cell) => cell === '')) {
    rows.pop();
  }
  return rows.map((row) => row.map((cell) => cell ?? ''));
}

export function useTableSelection({
  dataRows,
  columnCount,
  editMode,
  onCellEdit,
  onPasteCells,
  focusRef,
}: UseTableSelectionOptions) {
  const [selectedCell, setSelectedCell] = useState<CellPosition | null>(null);
  const [rangeAnchor, setRangeAnchor] = useState<CellPosition | null>(null);
  const [rangeEnd, setRangeEnd] = useState<CellPosition | null>(null);
  const [editingCell, setEditingCell] = useState<CellPosition | null>(null);

  useEffect(() => {
    if (!selectedCell) return;
    const clamp = (cell: CellPosition): CellPosition => ({
      row: Math.max(0, Math.min(dataRows.length - 1, cell.row)),
      col: Math.max(0, Math.min(columnCount - 1, cell.col)),
    });
    const nextSelected = clamp(selectedCell);
    if (nextSelected.row !== selectedCell.row || nextSelected.col !== selectedCell.col) {
      setSelectedCell(nextSelected);
    }
    if (rangeAnchor) {
      const nextAnchor = clamp(rangeAnchor);
      if (nextAnchor.row !== rangeAnchor.row || nextAnchor.col !== rangeAnchor.col) {
        setRangeAnchor(nextAnchor);
      }
    }
    if (rangeEnd) {
      const nextEnd = clamp(rangeEnd);
      if (nextEnd.row !== rangeEnd.row || nextEnd.col !== rangeEnd.col) {
        setRangeEnd(nextEnd);
      }
    }
    if (editingCell) {
      const nextEditing = clamp(editingCell);
      if (nextEditing.row !== editingCell.row || nextEditing.col !== editingCell.col) {
        setEditingCell(nextEditing);
      }
    }
  }, [columnCount, dataRows.length, editingCell, rangeAnchor, rangeEnd, selectedCell]);

  const selection = useMemo<SelectionRange | null>(() => {
    if (!selectedCell) return null;
    const anchor = rangeAnchor ?? selectedCell;
    const end = rangeEnd ?? selectedCell;
    const startRow = Math.min(anchor.row, end.row);
    const endRow = Math.max(anchor.row, end.row);
    const startCol = Math.min(anchor.col, end.col);
    const endCol = Math.max(anchor.col, end.col);
    return { anchor, end, startRow, endRow, startCol, endCol };
  }, [selectedCell, rangeAnchor, rangeEnd]);

  const selectCell = useCallback((cell: CellPosition, options?: { extend?: boolean }) => {
    const extend = options?.extend ?? false;
    if (extend) {
      const anchor = rangeAnchor ?? selectedCell ?? cell;
      setRangeAnchor(anchor);
      setRangeEnd(cell);
    } else {
      setRangeAnchor(cell);
      setRangeEnd(null);
    }
    setSelectedCell(cell);
    focusRef.current?.focus();
  }, [focusRef, rangeAnchor, selectedCell]);

  const enterEdit = useCallback((cell: CellPosition) => {
    if (!editMode || !onCellEdit) return;
    setEditingCell(cell);
  }, [editMode, onCellEdit]);

  const exitEdit = useCallback(() => {
    setEditingCell(null);
  }, []);

  const handleCopy = useCallback(() => {
    if (!selection) return;
    const rowsToCopy = [];
    for (let r = selection.startRow; r <= selection.endRow; r += 1) {
      const row = [];
      for (let c = selection.startCol; c <= selection.endCol; c += 1) {
        row.push(String((dataRows[r] ?? [])[c] ?? ''));
      }
      rowsToCopy.push(row);
    }
    if (rowsToCopy.length === 0) return;
    const tsv = rowsToCopy.map((row) => row.join('\t')).join('\n');
    void copyToClipboard(tsv);
  }, [dataRows, selection]);

  const handlePaste = useCallback(async (event: React.ClipboardEvent<HTMLDivElement>) => {
    if (!editMode || !onPasteCells || !selectedCell) return;
    event.preventDefault();
    const text = event.clipboardData?.getData('text/plain') ?? '';
    if (text) {
      const values = parseClipboardTsv(text);
      if (values.length > 0) {
        onPasteCells(selectedCell.row, selectedCell.col, values);
      }
      return;
    }
    try {
      const fallback = await navigator.clipboard.readText();
      const values = parseClipboardTsv(fallback);
      if (values.length > 0) {
        onPasteCells(selectedCell.row, selectedCell.col, values);
      }
    } catch {
      // no-op
    }
  }, [editMode, onPasteCells, selectedCell]);

  return {
    selectedCell,
    selection,
    editingCell,
    selectCell,
    enterEdit,
    exitEdit,
    handleCopy,
    handlePaste,
  };
}
